import type { Bridge, ConformanceTarget } from '@casa/bridge';
import { startDriverHost } from '@casa/driver-host';
import type { Reading } from '@casa/driver-sdk';
import { createDriverWorker } from './drivers.ts';

// The driver sandbox check of Phase 1 C4, run in a shell by scripts/conformance-report.mjs: a
// probe driver, loaded as an installed driver's module into the real driver Worker from /sandbox/,
// tries to reach the conformance peers through the host (the permission gate) and around it (the
// sandbox's policy). It may reach its declared host through the host, and nothing else.

export interface SandboxCheckResult {
    name: string;
    ok: boolean;
    detail: string;
}

/** The probe, as an installed driver's module would arrive: text, imported from a blob: URL. */
const PROBE_DRIVER = String.raw`
export default () => {
    let host;
    let config;
    let results = {};
    const outcome = async (action) => {
        try {
            const value = await action();
            return 'allowed' + (value === undefined ? '' : ' ' + value);
        } catch (error) {
            return 'refused: ' + ((error && error.message) || error);
        }
    };
    const violations = [];
    self.addEventListener('securitypolicyviolation', (event) => violations.push(event.violatedDirective + ' ' + event.blockedURI));

    return {
        async init(givenConfig, givenHost) {
            config = givenConfig;
            host = givenHost;
        },
        register() {},
        async refresh(due) {
            results = {
                hostFetchDeclared: await outcome(async () => (await host.fetch(config.url + '/echo')).status),
                hostFetchUndeclared: await outcome(() => host.fetch('http://casa-undeclared.invalid/')),
                hostWs: await outcome(() => host.ws(config.wsUrl)),
                hostTcp: await outcome(() => host.tcp(config.tcpHost, config.tcpPort)),
                directFetch: await outcome(async () => (await fetch(config.url + '/echo', { mode: 'no-cors' })).type),
                directWebSocket: await new Promise((resolve) => {
                    try {
                        const socket = new WebSocket(config.wsUrl);
                        const timer = setTimeout(() => resolve('no answer'), 3000);
                        socket.onopen = () => { clearTimeout(timer); socket.close(); resolve('allowed (opened)'); };
                        socket.onerror = () => { clearTimeout(timer); resolve('refused (error event)'); };
                    } catch (error) {
                        resolve('refused: ' + error);
                    }
                }),
                directImport: await outcome(async () => (await import(config.url + '/module.js')).default),
                // WebKit throws a SecurityError from the constructor where Chromium fires an error event.
                directEventSource: typeof EventSource === 'function' ? await new Promise((resolve) => {
                    try {
                        const source = new EventSource(config.url + '/echo');
                        const timer = setTimeout(() => { source.close(); resolve('no answer'); }, 3000);
                        source.onopen = () => { clearTimeout(timer); source.close(); resolve('allowed (opened)'); };
                        source.onerror = () => { clearTimeout(timer); source.close(); resolve('refused (error event)'); };
                    } catch (error) {
                        resolve('refused: ' + error);
                    }
                }) : 'not available',
            };
            // Violation events are dispatched asynchronously.
            await new Promise((resolve) => setTimeout(resolve, 200));
            results.violations = violations.join('; ') || 'none reported';
            for (const source of due) {
                host.notify(source);
            }
        },
        async read() {
            return [{ value: results }];
        },
        async dispose() {},
    };
};
`;

const EXPECTED: readonly [name: string, check: (outcome: string) => boolean][] = [
    ['host.fetch to the declared host is allowed', (outcome) => outcome === 'allowed 200'],
    ['host.fetch to an undeclared host is refused by the gate', (outcome) => outcome.startsWith('refused: not permitted: casa-undeclared.invalid')],
    ['host.ws without the ws permission is refused by the gate', (outcome) => outcome.startsWith('refused: not permitted') && outcome.includes('WebSocket')],
    ['host.tcp without the tcp permission is refused by the gate', (outcome) => outcome.startsWith('refused: not permitted') && outcome.includes('TCP')],
    ['fetch from the Worker is blocked by the sandbox', (outcome) => outcome.startsWith('refused')],
    ['WebSocket from the Worker is blocked by the sandbox', (outcome) => outcome.startsWith('refused')],
    ['import() of a remote module from the Worker is blocked by the sandbox', (outcome) => outcome.startsWith('refused')],
    ['EventSource from the Worker is blocked by the sandbox', (outcome) => outcome.startsWith('refused') || outcome === 'not available'],
];
const KEYS = ['hostFetchDeclared', 'hostFetchUndeclared', 'hostWs', 'hostTcp', 'directFetch', 'directWebSocket', 'directImport', 'directEventSource'];

export async function runSandboxCheck(bridge: Bridge, target: ConformanceTarget, timeoutMs = 20_000) : Promise<SandboxCheckResult[]> {
    const url = new URL(target.httpBase);
    let readings: Reading[] | null | undefined;
    const failures: string[] = [];
    const logged: string[] = [];
    const link = startDriverHost({
        bridge,
        createWorker: createDriverWorker,
        connection: 'sandbox-check',
        driver: { kind: 'module', code: PROBE_DRIVER },
        permissions: { hosts: [], configHosts: ['url'], ws: false, tcp: false },
        config: { url: url.origin, wsUrl: target.wsUrl, tcpHost: target.tcp.host, tcpPort: target.tcp.port },
        sources: [{ id: 'probe' }],
        events: {
            readings: (_sourceId, value) => {
                readings = value;
            },
            failed: (sourceId, reason) => failures.push(`${sourceId}: ${reason}`),
        },
        log: (message) => logged.push(message),
    });

    try {
        await Promise.race([
            link.refresh(['probe']),
            new Promise((_resolve, reject) => setTimeout(() => reject(new Error(`no answer from the probe driver in ${timeoutMs} ms`)), timeoutMs)),
        ]);
    } catch (error) {
        return [{ name: 'the probe driver runs in the sandbox', ok: false, detail: error instanceof Error ? error.message : String(error) }];
    } finally {
        link.dispose();
    }

    const results = readings?.[0]?.value as Record<string, string> | undefined;
    if (!results) {
        return [{ name: 'the probe driver runs in the sandbox', ok: false, detail: [...failures, ...logged].join('; ') || 'no readings' }];
    }

    return [
        ...EXPECTED.map(([name, check], index) => {
            const outcome = results[KEYS[index] ?? ''] ?? 'missing';

            return { name, ok: check(outcome), detail: outcome };
        }),
        { name: 'policy violations reported by the engine (information)', ok: true, detail: results.violations ?? '' },
        { name: 'refusals logged by the host', ok: logged.filter((line) => line.includes('refused')).length === 3, detail: logged.join(' | ') },
    ];
}
